import { FunctionComponent, useState, useCallback } from "react"
import { observer } from "mobx-react-lite"
import { makeStyles, mergeClasses } from '@griffel/react'
import { Caption1Strong, tokens } from "@fluentui/react-components"
import { GenericList } from "@knowmax/genericlist-core"
import { ToggleOpenButton } from "../core"
import { ResetFilters, RecycleBinFilter } from "."

const useClasses = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    padding: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorBrandBackground2,
    boxShadow: tokens.shadow8,
    position: 'sticky',
    overflowX: 'hidden',
    transitionProperty: 'row-gap',
    transitionDuration: '350ms',
    transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
  },
  rootOpen: {
    rowGap: tokens.spacingHorizontalM,
  },
  rootClosed: {
    rowGap: '0px',
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  headCollapsible: {
    cursor: 'pointer'
  },
  contents: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingHorizontalL,
    overflow: 'hidden',
    transitionProperty: 'max-height, opacity, transform',
    transitionDuration: '350ms',
    transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
  },
  contentsOpen: {
    maxHeight: '2000px', // Large enough to accommodate content
    opacity: '1',
    transform: 'translateY(0px)'
  },
  contentsClosed: {
    maxHeight: '0px',
    opacity: '0',
    transform: 'translateY(-8px)'
  }
})

/** Wrap this around components to be put on a fancy side panel besides list. Renders as div. */
export const SidePanel: FunctionComponent<{
  children?: React.ReactNode,
  caption?: string,
  showResetFilters?: boolean,
  showRecycleBinFilter?: boolean,
  collapsible?: boolean,
  collapsed?: boolean,
  list?: GenericList,
  listId?: string,
  className?: string,
  captionClassName?: string,
}> = observer(({
  children,
  caption,
  showResetFilters = true,
  showRecycleBinFilter = false,
  collapsible = false,
  collapsed = false,  
  list,
  listId,
  className,
  captionClassName,
}) => {
  const [open, setOpen] = useState(!collapsed || !collapsible)
  const classes = useClasses()

  const toggle = useCallback(() => {
    if (collapsible) {
      setOpen(o => !o)
    }
  }, [collapsible])

  return (
    <div className={mergeClasses(
      classes.root,
      open ? classes.rootOpen : classes.rootClosed,
      className
    )}>
      <section className={mergeClasses(classes.head, collapsible && classes.headCollapsible)} onClick={toggle} aria-expanded={collapsible ? open : undefined}>
        {caption &&
          <Caption1Strong className={captionClassName}>{caption}</Caption1Strong>
        }
        {collapsible && <ToggleOpenButton value={open} />}
      </section>

      <section className={mergeClasses(
        classes.contents,
        open ? classes.contentsOpen : classes.contentsClosed
      )}>
        {showResetFilters && <ResetFilters list={list} listId={listId} />}
        {showRecycleBinFilter && <RecycleBinFilter listId={listId} />}
        {children}
      </section>
    </div>
  )
})